import { FC } from 'react'
import classNames from 'classnames'
import { components, OptionProps } from 'react-select'

import Loader from 'components/base/Loader'
import { DeleteIcon } from 'components/icons'
import { GroupRole } from 'common/types/enums'
import Switch from 'components/base/forms/Switch'
import Select from 'components/base/forms/Select'
import { openConfirm } from 'components/base/Modal'
import Checkbox from 'components/base/forms/Checkbox'
import { ErrorMessage } from 'components/base/Messages'
import InputGroup from 'components/base/forms/InputGroup'
import { GroupMember, TeamMember } from 'common/types/responses'
import { useGetTeamMembersQuery } from 'common/services/useTeamMember'

type MemberOption = {
  value: TeamMember & { user_id: number }
}

type AddRemoveMembersType = {
  groupMembers: GroupMember[]
  setGroupMembers: (v: GroupMember[]) => void
}

const AddRemoveMembers: FC<AddRemoveMembersType> = ({
  groupMembers,
  setGroupMembers,
}) => {
  const { data, error, isFetching } = useGetTeamMembersQuery({ page_size: 100 })
  const teamMembers = data?.results

  const handleMemberAdd = (v: MemberOption | null) => {
    const newMember = v?.value
    if (!newMember || groupMembers.some((m) => m.user_id === newMember.id))
      return

    setGroupMembers([
      ...groupMembers,
      {
        ...newMember,
        role: groupMembers.length === 0 ? GroupRole.OWNER : GroupRole.MEMBER,
      },
    ])
  }

  const handleRoleChange = (memberId: number, checked: boolean) => {
    if (!checked) {
      const ownerCount = groupMembers.filter(
        (m) => m.role === GroupRole.OWNER,
      ).length
      if (ownerCount <= 1) {
        return
      }
    }
    setGroupMembers(
      groupMembers.map((u) => ({
        ...u,
        role:
          u.id === memberId
            ? checked
              ? GroupRole.OWNER
              : GroupRole.MEMBER
            : GroupRole.MEMBER,
      })),
    )
  }

  const MemberOptionComponent = (props: OptionProps<MemberOption>) => {
    const { first_name, id, last_name } = props.data.value || {}
    const isMember = groupMembers.find((v) => v.user_id === id)
    return (
      <components.Option {...props}>
        <div>
          <div className='d-flex justify-content-between'>
            <div className='fw-semibold'>{`${first_name} ${last_name}`}</div>
            <Checkbox label={''} checked={!!isMember} id={String(id)} />
          </div>
        </div>
      </components.Option>
    )
  }

  return (
    <>
      {data ? (
        <div>
          <InputGroup
            title='Add a Team Member'
            component={
              <Select<MemberOption>
                components={{
                  Option: MemberOptionComponent,
                }}
                closeMenuOnSelect={false}
                hideSelectedOptions={false}
                value={[]}
                onChange={(v) => handleMemberAdd(v)}
                options={teamMembers?.map((v) => ({
                  value: { ...v, user_id: v.id },
                }))}
              />
            }
          />

          <div className='mt-5'>
            <span className='fw-semibold fs-lg d-block mb-4'>Team Members</span>
            {!groupMembers?.length && (
              <span className='text-muted'>No team members</span>
            )}
            {groupMembers.map((v) => (
              <div
                key={v.id}
                className='d-flex justify-content-between bg-light150 px-4 py-2 rounded mb-4'
              >
                <div className='d-flex flex-column'>
                  <span className='fw-semibold'>
                    {v.first_name} {v.last_name}
                  </span>
                  <span>{v.email}</span>
                </div>
                <div className='d-flex align-items-center gap-6'>
                  <div>
                    <span className='fs-xss text-muted'>Group Owner</span>
                    <Switch
                      checked={v.role === GroupRole.OWNER}
                      onChange={(checked) => handleRoleChange(v.id, checked)}
                    />
                  </div>
                  <DeleteIcon
                    className={classNames('text-danger', {
                      'cursor-pointer': v.role !== GroupRole.OWNER,
                      'opacity-50': v.role === GroupRole.OWNER,
                    })}
                    onClick={() => {
                      if (
                        v.role === GroupRole.OWNER &&
                        groupMembers.filter((m) => m.role === GroupRole.OWNER)
                          .length <= 1
                      ) {
                        return
                      }
                      openConfirm(
                        'Confirm remove user',
                        'Are you sure you wish to remove this user?',
                        (close: () => void) => {
                          close()
                          setGroupMembers(
                            groupMembers.filter((u) => v.id !== u.id),
                          )
                        },
                      )
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : isFetching ? (
        <div className={'text-center'}>
          <Loader />
        </div>
      ) : (
        <div className={'text-center'}>
          <ErrorMessage>{error}</ErrorMessage>
        </div>
      )}
    </>
  )
}

export default AddRemoveMembers
